import React, { useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useParams } from 'react-router-dom'
import DownloadIcon from '@mui/icons-material/Download'
import SendIcon from '@mui/icons-material/Send'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { DownloadInvoicePdf, EmailInvoicePdf } from '../../REST/invoices'

const schema = yup.object().shape({
    email: yup.string().email('Invalid email').required('Email is required'),
    subject: yup.string().required('Subject is required'),
})

export default function InvoiceCopyDialog({ invoiceData = {}, tenant = '' }) {
    const { id } = useParams()
    const [open, setOpen] = useState(false)
    const [emailOpen, setEmailOpen] = useState(false)
    const [sending, setSending] = useState(false)

    const { control, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            email: '',
            subject: `Invoice ${invoiceData.invoiceNumber || ''}`,
        }
    })

    const handleDownload = async () => {
        const resp = await DownloadInvoicePdf(id)
        const url = window.URL.createObjectURL(new Blob([resp.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Invoice-${invoiceData.invoiceNumber || id}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
    }

    const handleEmailSubmit = async (values) => {
        setSending(true)
        await EmailInvoicePdf(id, values)
        setSending(false)
        setEmailOpen(false)
        reset()
    }

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => setOpen(true)}
                sx={{ alignSelf: 'flex-start' }}
            >
                Get a Copy
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Get a Copy of Invoice</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Download the invoice as a PDF or send it via email.
                        </Typography>

                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            fullWidth
                        >
                            Download PDF
                        </Button>

                        <Divider>or</Divider>

                        <Button
                            variant="outlined"
                            startIcon={<SendIcon />}
                            onClick={() => { setOpen(false); setEmailOpen(true) }}
                            fullWidth
                        >
                            Send via Email
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Email Invoice</DialogTitle>
                <form onSubmit={handleSubmit(handleEmailSubmit)}>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Controller name="email" control={control}
                                render={({ field }) => (
                                    <TextField {...field} label="Recipient Email" fullWidth
                                        error={!!errors.email} helperText={errors.email?.message} />
                                )} />
                            <Controller name="subject" control={control}
                                render={({ field }) => (
                                    <TextField {...field} label="Subject" fullWidth
                                        error={!!errors.subject} helperText={errors.subject?.message} />
                                )} />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={sending}>
                            {sending ? 'Sending…' : 'Send'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    )
}
